import React, { FC, useEffect, useMemo } from "react";
import moment from "moment";
import CustomTable from "../../../components/Tables/CustomTable";
import ClickableUserName from "../../../components/shared/ClickableUserName";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { fetchAllUsedCoupons } from "../../../redux/features/CouponSlice";
import { UsedCouponItem } from "../../../redux/features/types/couponListTypes";
import CouponFilterBar, { prettifyCouponType } from "./CouponFilterBar";
import { useCouponFilters } from "./useCouponFilters";

// Each row is a redemption event ("buyer X used coupon Y on date Z"), not a
// coupon. The headline column is therefore Applied — that's the field unique
// to this view. Coupon fields live under `coupon`; the buyer under
// `assignedTo`. We deliberately keep the column order parallel to All Coupons
// (Code · Type · Value · ... · Expires · Status) so the two tabs feel like
// the same surface viewed two ways.
const renderBuyer = (assignedTo: UsedCouponItem["assignedTo"] | undefined) => {
  if (!assignedTo) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-col min-w-0">
      <ClickableUserName user={assignedTo} userType="BUYER" />
      {assignedTo.email && (
        <span
          className="text-xs text-gray-500 truncate"
          title={assignedTo.email}
        >
          {assignedTo.email}
        </span>
      )}
    </div>
  );
};

const formatValue = (coupon: UsedCouponItem["coupon"]): string => {
  if (coupon?.couponAmount !== null && coupon?.couponAmount !== undefined) {
    return `₦${coupon.couponAmount}`;
  }
  if (coupon?.couponDiscount !== null && coupon?.couponDiscount !== undefined) {
    return `${coupon.couponDiscount}%`;
  }
  return "N/A";
};

const UsedCouponsTable: FC = () => {
  const dispatch = useAppDispatch();
  const { usedCoupons, loading } = useAppSelector((state) => state.coupons);

  const filters = useCouponFilters();
  const { page, setPage, setLimit, hasActiveFilters, query } = filters;

  const tableData = useMemo(
    () => ({
      columns: [
        { label: "Code", field: "code" },
        { label: "Coupon Type", field: "type" },
        { label: "Value", field: "value" },
        { label: "Assigned", field: "assigned" },
        { label: "Applied", field: "applied" },
        { label: "Expires", field: "expiry" },
        { label: "Buyer", field: "buyer" },
        { label: "Status", field: "status" },
      ],
      rows: (usedCoupons?.data?.items ?? []).map(
        (item: UsedCouponItem, index: number) => {
          const expiryDate = item?.coupon?.expiryDate;
          const isExpired = expiryDate
            ? new Date(expiryDate) < new Date()
            : false;

          return {
            id: index + 1,
            code: item?.coupon?.couponCode ?? "—",
            type: prettifyCouponType(item?.coupon?.couponType as string),
            value: formatValue(item?.coupon),
            buyer: renderBuyer(item?.assignedTo),
            assigned: item?.assignedAt
              ? moment(item.assignedAt).format("Do MMM YYYY")
              : "—",
            applied: item?.dateApplied
              ? moment(item.dateApplied).format("Do MMM YYYY")
              : "—",
            expiry: expiryDate
              ? moment(expiryDate).format("Do MMM YYYY")
              : "—",
            status: (
              <span
                className={`text-xs font-semibold px-3 py-1 rounded-full ${
                  isExpired
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {isExpired ? "Expired" : "Active"}
              </span>
            ),
          };
        }
      ),
    }),
    [usedCoupons]
  );

  useEffect(() => {
    dispatch(fetchAllUsedCoupons(query));
  }, [dispatch, query]);

  return (
    <>
      <CouponFilterBar filters={filters} panelId="used-coupons-filter-panel" />

      <CustomTable
        data={tableData}
        noRecordText={
          hasActiveFilters
            ? "No used coupons match the current filters"
            : "No Used Coupons Available"
        }
        loading={loading}
        showFooter={true}
        totalPages={usedCoupons?.data?.meta?.totalPages}
        itemsPerPage={usedCoupons?.data?.meta?.itemsPerPage}
        currentPage={page}
        onPageChange={(value: number) => setPage(value)}
        onLimitChange={(value: number) => {
          setLimit(value);
          setPage(0);
        }}
      />
    </>
  );
};

export default UsedCouponsTable;
